<script setup lang="ts">
import type { Chapter } from '@/api/do-question/doQuestion.ts';
import type { VisualizationResource } from '@/api/resource/visualizations';

import type { DrawerItem } from '@/components/subject/configs.ts';
import { Icon } from '@iconify/vue';
import { Book, BookmarkX, BriefcaseBusiness, Codepen, FileUp, FlaskConical, Home, Link2, Network, Server, Share, User } from 'lucide-vue-next';
import { TreeItem, TreeRoot } from 'radix-vue';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { fetchChapterListApi, fetchSubjectListApi } from '@/api/do-question/doQuestion.ts';
import { fetchApprovedVisualizationListApi, fetchMyVisualizationApi, fetchVisualizationListApi, fetchVisualizationMenuOrderApi, saveVisualizationMenuOrderApi } from '@/api/resource/visualizations';
import BoxSelector from '@/components/selector/BoxSelector.vue';
import { experimentConfigs, subjectConfig } from '@/components/subject/configs.ts';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useConfigStore, useUserStore } from '@/store';
import { isPlatformAdminUser, isVisitor } from '@/utils/auth.ts';
import { error as showError, success as showSuccess } from '@/utils/toast';
import { buildPreviewVisualization, PREVIEW_REVIEW_CHANGED_EVENT, PREVIEW_VISUALIZATION_ID, SECOND_PREVIEW_VISUALIZATION_ID, THIRD_PREVIEW_VISUALIZATION_ID } from '@/utils/visualizationReviewPreview';

interface SideBarItem {
  label: string;
  icon: any;
  route?: string;
  command?: () => void;
  activeRoutes?: string[];
  activeWhenDrawerOpen?: 'experiment' | 'question';
  exactMatch?: boolean;
}

const route = useRoute();
const openSubjectSelector = ref(false);
const openExperimentDrawer = ref(false);
const openQuestionDrawer = ref(false);
const subjectSelector = ref<typeof BoxSelector | null>(null);

function toggleExperimentDrawer() {
  openExperimentDrawer.value = !openExperimentDrawer.value;
  openQuestionDrawer.value = false;
  if (openExperimentDrawer.value)
    refreshPendingCourseVisualizations();
}

function toggleQuestionDrawer() {
  openQuestionDrawer.value = !openQuestionDrawer.value;
  openExperimentDrawer.value = false;
}

function isActiveRoute(itemRoute: string, restrict = false): boolean {
  return route.path === itemRoute || (!restrict && route.path.startsWith(`${itemRoute}/`));
}

function getOpenDrawerKey(): SideBarItem['activeWhenDrawerOpen'] | null {
  if (openExperimentDrawer.value)
    return 'experiment';
  if (openQuestionDrawer.value)
    return 'question';
  return null;
}

function isSidebarItemActive(item: SideBarItem): boolean {
  const openDrawerKey = getOpenDrawerKey();
  if (openDrawerKey)
    return item.activeWhenDrawerOpen === openDrawerKey;

  const activeRoutes = item.activeRoutes?.length
    ? item.activeRoutes
    : item.route
      ? [item.route]
      : [];

  return activeRoutes.some(activeRoute => isActiveRoute(activeRoute, item.exactMatch ?? activeRoute === '/'));
}

const router = useRouter();
const visitorAllowedItem = [
  '主页',
  '实验目录',
  '做题目录',
];
const userStore = useUserStore();
const configStore = useConfigStore();
const isDynamicCourse = computed(() => Boolean(configStore.dynamicSubject));
const isBackendAllowed = computed(() => isPlatformAdminUser(userStore.user));
const isTeacherCoursePreview = computed(() =>
  isDynamicCourse.value
  && Number(userStore.user?.role) === 2,
);
const rejectedOwnVisualizationCount = ref(0);
const isVisualizationPreviewCourse = computed(() =>
  String(configStore.currentSubject?.id_ ?? '') === '36'
  || String(configStore.currentSubject?.name ?? '').trim() === '概率',
);
const sideBarItem = ref<SideBarItem[]>([
  {
    label: '主页',
    icon: Home,
    route: '/',
    exactMatch: true,
  },
  {
    label: '实验目录',
    icon: FlaskConical,
    route: '/experiment',
    command: toggleExperimentDrawer,
    activeWhenDrawerOpen: 'experiment',
  },
  {
    label: '做题目录',
    icon: Book,
    route: '/question',
    command: toggleQuestionDrawer,
    activeRoutes: ['/question', '/mindmap/question'],
    activeWhenDrawerOpen: 'question',
  },
  {
    label: '错题本',
    icon: BookmarkX,
    route: '/mistakerecord',
  },
  {
    label: '可视化上传',
    icon: FileUp,
    route: '/visualization',
  },
  {
    label: '共享资源',
    icon: Share,
    route: '/share-resource',
  },
]);

const visibleSideBarItems = computed(() => sideBarItem.value);

interface ApprovedVisualizationMenuItem extends DrawerItem {
  approvedAtMs: number;
  visualizationId: number;
  visualizationChapterId: number;
  displayOrder: number;
  canReorder: boolean;
  chapterName: string;
  expId?: string;
}

const approvedVisualizationItems = ref<ApprovedVisualizationMenuItem[]>([]);
const dynamicCourseChapters = ref<Chapter[]>([]);
const pendingCourseVisualizations = ref<VisualizationResource[]>([]);
const loadingPendingCourseVisualizations = ref(false);
const pendingVisualizationReviewCount = computed(() => pendingCourseVisualizations.value.length);
const draggedVisualizationId = ref<string | null>(null);
const dragOverVisualizationId = ref<string | null>(null);
const suppressExperimentClick = ref(false);
const isSavingVisualizationOrder = ref(false);
const experimentOrderVersion = ref(0);
const openExperimentOrderConfirm = ref(false);
const pendingExperimentOrder = ref<{
  groupKey: string;
  orderedKeys: string[];
  sourceTitle: string;
  targetTitle: string;
} | null>(null);
const canReorderVisualizations = computed(() =>
  isPlatformAdminUser(userStore.user)
  || approvedVisualizationItems.value.some(item => item.canReorder),
);

function openCourseWorkspace() {
  const courseId = configStore.currentSubject?.id_;
  if (!courseId)
    return;
  router.push(`/courses/${courseId}/manage`);
}

function sortLinearAlgebraEnExperiments(items: DrawerItem[]) {
  if (configStore.currentSubjectId !== 'linear-algebra-en')
    return items;

  const getFirstNumber = (title: string) => {
    const match = title.match(/\d+/);
    return match ? Number.parseInt(match[0], 10) : Number.POSITIVE_INFINITY;
  };

  return [...items].sort((a, b) => getFirstNumber(a.title) - getFirstNumber(b.title));
}

const experimentItems = computed<DrawerItem[] | null>(() => {
  if (!configStore.currentSubject)
    return null;

  const source = isDynamicCourse.value
    ? dynamicCourseChapters.value.map(chapter => ({
        title: chapter.name,
        icon: 'lucide:folder',
        children: [],
      }))
    : (experimentConfigs as any)[configStore.currentSubjectId!]?.experiments || [];
  const copied: DrawerItem[] = (source as any[]).map((item: any) => ({
    ...item,
    children: item.children ? [...item.children] : item.children,
  }));

  const routeSetByChapter = new Map<string, Set<string>>();

  const collectRoutes = (items: DrawerItem[] | undefined, result: Set<string>) => {
    if (!Array.isArray(items))
      return;
    items.forEach((child) => {
      if (child.route)
        result.add(child.route);
      if (Array.isArray(child.children) && child.children.length > 0)
        collectRoutes(child.children, result);
    });
  };

  const getRouteSet = (chapter: DrawerItem) => {
    const key = chapter.title;
    if (!routeSetByChapter.has(key)) {
      const current = new Set<string>();
      collectRoutes(chapter.children, current);
      routeSetByChapter.set(key, current);
    }
    return routeSetByChapter.get(key)!;
  };

  const findRelatedExamplesFolder = (chapter: DrawerItem) => {
    if (!Array.isArray(chapter.children))
      return null;
    return chapter.children.find((item) => {
      if (!Array.isArray(item.children))
        return false;
      const title = String(item.title || '').replace(/\s+/g, '');
      return title === '相关实例与图表';
    }) ?? null;
  };

  const findMatchedChapter = (chapterName: string) => {
    const backendFull = normalizeChapterText(chapterName);
    const backendCore = normalizeChapterText(removeChapterPrefix(chapterName));

    return copied.find((item) => {
      const groupFull = normalizeChapterText(item.title);
      const groupCore = normalizeChapterText(removeChapterPrefix(item.title));

      if (!groupFull)
        return false;

      const fullMatched = backendFull && (groupFull === backendFull || groupFull.includes(backendFull) || backendFull.includes(groupFull));
      if (fullMatched)
        return true;

      if (backendCore.length < 2 || groupCore.length < 2)
        return false;
      return groupCore === backendCore || groupCore.includes(backendCore) || backendCore.includes(groupCore);
    });
  };

  const findDynamicRootChapter = (item: any) => {
    if (!isDynamicCourse.value)
      return null;
    const targetId = String(item?.chapter?.id ?? item?.chapter_id ?? item?.visualizationChapterId ?? '');
    const targetName = normalizeChapterText(item?.chapter?.name ?? item?.chapter_name ?? item?.chapterName ?? '');
    const containsChapter = (chapter: any): boolean =>
      Boolean(
        (targetId && String(chapter?.id ?? '') === targetId)
        || (targetName && normalizeChapterText(chapter?.name ?? chapter?.title ?? '') === targetName),
      )
      || (Array.isArray(chapter?.children) && chapter.children.some(containsChapter));
    const rootIndex = dynamicCourseChapters.value.findIndex(containsChapter);
    return rootIndex >= 0 ? copied[rootIndex] ?? null : null;
  };

  approvedVisualizationItems.value.forEach((dynamicItem) => {
    const chapterName = dynamicItem.chapterName || '其他章节';
    let chapter = findDynamicRootChapter(dynamicItem) ?? findMatchedChapter(chapterName);

    if (!chapter) {
      chapter = {
        title: chapterName,
        icon: 'lucide:folder',
        children: [],
      };
      copied.push(chapter);
    }

    const routeSet = getRouteSet(chapter);
    if (dynamicItem.route && routeSet.has(dynamicItem.route))
      return;

    const nextItem: DrawerItem = {
      title: dynamicItem.title,
      icon: dynamicItem.icon,
      route: dynamicItem.route,
    };
    Object.assign(nextItem, {
      visualizationId: dynamicItem.visualizationId,
      visualizationChapterId: dynamicItem.visualizationChapterId,
      canReorder: dynamicItem.canReorder,
    });

    const shouldPlaceInRelatedFolder = isProbabilityThirdChapter(chapter.title) && isRelatedExampleExpId(dynamicItem.expId);
    if (shouldPlaceInRelatedFolder) {
      const folder = findRelatedExamplesFolder(chapter);
      if (folder) {
        const folderChildren = folder.children ? [...folder.children] : [];
        folderChildren.push(nextItem);
        folder.children = folderChildren;
      }
      else {
        const currentChildren = chapter.children ? [...chapter.children] : [];
        currentChildren.push(nextItem);
        chapter.children = currentChildren;
      }
    }
    else {
      const currentChildren = chapter.children ? [...chapter.children] : [];
      currentChildren.push(nextItem);
      chapter.children = currentChildren;
    }

    if (dynamicItem.route)
      routeSet.add(dynamicItem.route);
  });

  if (import.meta.env.MODE === 'local-acceptance' && isDynamicCourse.value && isVisualizationPreviewCourse.value) {
    const previewConfigs = [
      { visualizationId: PREVIEW_VISUALIZATION_ID, chapter: dynamicCourseChapters.value[0], fallback: '第一章 概率论的基本概念' },
      { visualizationId: SECOND_PREVIEW_VISUALIZATION_ID, chapter: dynamicCourseChapters.value[0], fallback: '第一章 概率论的基本概念' },
      { visualizationId: THIRD_PREVIEW_VISUALIZATION_ID, chapter: dynamicCourseChapters.value[1], fallback: '第二章 随机变量及其分布' },
    ];
    previewConfigs.map(({ visualizationId, chapter, fallback }) => buildPreviewVisualization({
      courseId: String(configStore.currentSubject!.id_),
      courseName: String(configStore.currentSubject!.name),
      chapterId: chapter?.id,
      chapterName: chapter?.name || fallback,
      visualizationId,
    })).filter(preview => preview.review_status === 'approved').forEach((publishedPreview) => {
      const chapterName = visualizationChapterName(publishedPreview);
      let chapter = findMatchedChapter(chapterName);
      if (!chapter) {
        chapter = { title: chapterName, icon: 'lucide:folder', children: [] };
        copied.push(chapter);
      }
      const route = `/experiment/visualization/${publishedPreview.id}`;
      const routeSet = getRouteSet(chapter);
      if (!routeSet.has(route)) {
        chapter.children = [...(chapter.children ?? []), {
          title: publishedPreview.name,
          icon: 'lucide:flask-conical',
          route,
        }];
        routeSet.add(route);
      }
    });
  }

  pendingCourseVisualizations.value.forEach((pendingItem) => {
    const chapterName = visualizationChapterName(pendingItem);
    let chapter = findDynamicRootChapter(pendingItem) ?? findMatchedChapter(chapterName);
    if (!chapter) {
      chapter = { title: chapterName, icon: 'lucide:folder', children: [] };
      copied.push(chapter);
    }

    const route = `/experiment/visualization/${pendingItem.id}`;
    const routeSet = getRouteSet(chapter);
    if (routeSet.has(route))
      return;
    const nextItem: DrawerItem = {
      title: pendingItem.name,
      icon: 'lucide:flask-conical',
      route: `${route}?review=pending`,
    };
    Object.assign(nextItem, { reviewStatus: 'pending', visualizationId: pendingItem.id });
    chapter.children = [...(chapter.children ?? []), nextItem];
    routeSet.add(route);
  });

  return sortLinearAlgebraEnExperiments(applySavedExperimentOrder(copied));
});
const questionItems = ref<DrawerItem[]>([]);
const universitySubjects = computed(() => Object.values(subjectConfig).filter(subject => !subject.id.startsWith('high-school-')));

function normalizeChapterText(value: string) {
  return String(value || '')
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/[：:·、，,。.\-_/()（）[\]【】]/g, '');
}

function removeChapterPrefix(value: string) {
  return String(value || '')
    .replace(/^第?[一二三四五六七八九十百千万0-9]+章/, '')
    .replace(/^[：:\-\s]+/, '');
}

function isProbabilityThirdChapter(chapterName: string) {
  if (!['probability', 'probability-bilingual'].includes(String(configStore.currentSubjectId ?? '')))
    return false;
  const normalized = String(chapterName || '').replace(/\s+/g, '');
  return normalized.startsWith('第三章') || normalized.startsWith('第3章') || normalized.startsWith('3.');
}

function isRelatedExampleExpId(expId: unknown) {
  const value = String(expId || '').trim();
  const match = /^PT-exp-(\d{5})$/i.exec(value);
  if (!match)
    return false;
  const serial = Number.parseInt(match[1], 10);
  return Number.isFinite(serial) && serial >= 3002 && serial <= 3026;
}

function normalizeVisualizationExpId(raw: unknown) {
  const value = String(raw || '').trim();
  return /^PT-exp-\d{5}$/i.test(value) ? value : '';
}

function regroupRelatedExamples(chapter: DrawerItem) {
  if (!isProbabilityThirdChapter(chapter.title) || !Array.isArray(chapter.children) || chapter.children.length === 0)
    return chapter;

  const related: DrawerItem[] = [];
  const remain: DrawerItem[] = [];
  let insertAt = -1;

  chapter.children.forEach((item) => {
    const isLeaf = !Array.isArray(item.children) || item.children.length === 0;
    if (isLeaf && isRelatedExampleExpId((item as any).chapterId)) {
      if (insertAt < 0)
        insertAt = remain.length;
      related.push(item);
      return;
    }
    remain.push(item);
  });

  if (related.length === 0)
    return chapter;

  const folderItem: DrawerItem = {
    title: '相关实例与图表',
    icon: 'lucide:folder',
    children: related,
  };

  const targetIndex = insertAt < 0 ? remain.length : insertAt;
  remain.splice(targetIndex, 0, folderItem);
  chapter.children = remain;
  return chapter;
}

function resolveApprovedAtMs(item: any) {
  const candidates = [
    item?.approved_at,
    item?.approvedAt,
    item?.reviewed_at,
    item?.reviewedAt,
    item?.review_at,
    item?.reviewAt,
    item?.updated_at,
    item?.updatedAt,
    item?.created_at,
    item?.createdAt,
  ];

  for (const v of candidates) {
    if (!v)
      continue;
    const ts = new Date(v).getTime();
    if (!Number.isNaN(ts))
      return ts;
  }
  return Number.MAX_SAFE_INTEGER;
}

function getExperimentOrderStorageKey() {
  return `experiment-menu-order:v1:${String(configStore.currentSubject?.id_ ?? configStore.currentSubjectId ?? '')}`;
}

function readSavedExperimentOrder(): Record<string, string[]> {
  try {
    return JSON.parse(localStorage.getItem(getExperimentOrderStorageKey()) || '{}');
  }
  catch {
    return {};
  }
}

function applySavedExperimentOrder(items: DrawerItem[]) {
  void experimentOrderVersion.value;
  const savedOrder = readSavedExperimentOrder();

  const decorate = (entries: DrawerItem[], parentPath: string[]): DrawerItem[] => {
    const groupKey = parentPath.join(' / ');
    const decorated = entries.map((entry) => {
      const next = { ...entry } as DrawerItem;
      if (Array.isArray(entry.children) && entry.children.length > 0) {
        next.children = decorate(entry.children, [...parentPath, entry.title]);
      }
      else if (entry.route) {
        const visualizationId = Number((entry as any).visualizationId);
        Object.assign(next, {
          experimentMenuKey: Number.isInteger(visualizationId) && visualizationId > 0
            ? `visualization:${visualizationId}`
            : `route:${entry.route}`,
          experimentMenuGroup: groupKey,
        });
      }
      return next;
    });

    const positions = new Map((savedOrder[groupKey] || []).map((key, index) => [key, index]));
    if (!decorated.every(entry => getExperimentMenuKey(entry) !== null))
      return decorated;
    return decorated
      .map((entry, originalIndex) => ({ entry, originalIndex }))
      .sort((a, b) => {
        const aPosition = positions.get(String((a.entry as any).experimentMenuKey || ''));
        const bPosition = positions.get(String((b.entry as any).experimentMenuKey || ''));
        if (aPosition === undefined && bPosition === undefined)
          return a.originalIndex - b.originalIndex;
        if (aPosition === undefined)
          return 1;
        if (bPosition === undefined)
          return -1;
        return aPosition - bPosition;
      })
      .map(({ entry }) => entry);
  };

  return decorate(items, []);
}

function getExperimentMenuKey(item: DrawerItem) {
  const value = String((item as any).experimentMenuKey || '');
  return value || null;
}

function getExperimentMenuGroup(item: DrawerItem) {
  return String((item as any).experimentMenuGroup || '');
}

function collectExperimentMenuItems() {
  const result: DrawerItem[] = [];
  const visit = (entries: DrawerItem[] | null | undefined) => {
    if (!entries)
      return;
    entries.forEach((entry) => {
      if (getExperimentMenuKey(entry))
        result.push(entry);
      if (entry.children)
        visit(entry.children);
    });
  };
  visit(experimentItems.value);
  return result;
}

function findExperimentMenuGroup(experimentKey: string | null) {
  if (!experimentKey)
    return '';
  const item = collectExperimentMenuItems().find(entry => getExperimentMenuKey(entry) === experimentKey);
  return item ? getExperimentMenuGroup(item) : '';
}

function canDragVisualization(item: DrawerItem) {
  const hasPermission = isPlatformAdminUser(userStore.user)
    || Boolean((item as any).canReorder);
  return canReorderVisualizations.value && hasPermission && getExperimentMenuKey(item) !== null;
}

function handleVisualizationDragStart(event: DragEvent, item: DrawerItem) {
  if (!canDragVisualization(item)) {
    event.preventDefault();
    return;
  }
  const experimentKey = getExperimentMenuKey(item)!;
  draggedVisualizationId.value = experimentKey;
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', experimentKey);
  }
}

function handleVisualizationDragOver(event: DragEvent, item: DrawerItem) {
  const targetKey = getExperimentMenuKey(item);
  const sourceGroup = findExperimentMenuGroup(draggedVisualizationId.value);
  const targetGroup = getExperimentMenuGroup(item);
  if (!targetKey || !sourceGroup || sourceGroup !== targetGroup)
    return;
  event.preventDefault();
  if (event.dataTransfer)
    event.dataTransfer.dropEffect = 'move';
  dragOverVisualizationId.value = targetKey;
}

function handleVisualizationDragEnd() {
  draggedVisualizationId.value = null;
  dragOverVisualizationId.value = null;
}

function handleVisualizationDrop(event: DragEvent, item: DrawerItem) {
  event.preventDefault();
  const sourceKey = draggedVisualizationId.value;
  const targetKey = getExperimentMenuKey(item);
  const groupKey = getExperimentMenuGroup(item);
  handleVisualizationDragEnd();

  if (!sourceKey || !targetKey || sourceKey === targetKey || findExperimentMenuGroup(sourceKey) !== groupKey)
    return;
  if (isSavingVisualizationOrder.value)
    return;

  const groupItems = collectExperimentMenuItems().filter(entry => getExperimentMenuGroup(entry) === groupKey);
  const orderedKeys = groupItems.map(entry => getExperimentMenuKey(entry)!).filter(Boolean);
  const sourceIndex = orderedKeys.indexOf(sourceKey);
  const targetIndex = orderedKeys.indexOf(targetKey);
  if (sourceIndex < 0 || targetIndex < 0)
    return;
  const [moved] = orderedKeys.splice(sourceIndex, 1);
  if (!moved)
    return;
  orderedKeys.splice(targetIndex, 0, moved);

  suppressExperimentClick.value = true;
  window.setTimeout(() => {
    suppressExperimentClick.value = false;
  }, 0);

  const sourceItem = groupItems.find(entry => getExperimentMenuKey(entry) === sourceKey);
  pendingExperimentOrder.value = {
    groupKey,
    orderedKeys,
    sourceTitle: sourceItem?.title || '所选实验',
    targetTitle: item.title || '目标位置',
  };
  openExperimentOrderConfirm.value = true;
}

function cancelExperimentOrderChange() {
  if (isSavingVisualizationOrder.value)
    return;
  openExperimentOrderConfirm.value = false;
  pendingExperimentOrder.value = null;
}

async function confirmExperimentOrderChange() {
  const pending = pendingExperimentOrder.value;
  if (!pending || isSavingVisualizationOrder.value)
    return;

  try {
    isSavingVisualizationOrder.value = true;
    await saveVisualizationMenuOrderApi(
      String(configStore.currentSubject?.id_ ?? ''),
      pending.groupKey,
      pending.orderedKeys,
    );

    const savedOrder = readSavedExperimentOrder();
    savedOrder[pending.groupKey] = pending.orderedKeys;
    localStorage.setItem(getExperimentOrderStorageKey(), JSON.stringify(savedOrder));
    experimentOrderVersion.value += 1;
    openExperimentOrderConfirm.value = false;
    pendingExperimentOrder.value = null;
    showSuccess('实验顺序已保存');
  }
  catch (error) {
    console.error('保存实验顺序失败', error);
    showError('实验顺序保存失败，目录未做调整，请稍后重试');
  }
  finally {
    isSavingVisualizationOrder.value = false;
  }
}

function handleExperimentItemClick(item: DrawerItem) {
  if (suppressExperimentClick.value || !item.route)
    return;
  router.push(item.route);
  openExperimentDrawer.value = false;
}

async function refreshApprovedVisualizationItems() {
  const subjectId = String(configStore.currentSubject?.id_ ?? '').trim();
  if (!subjectId) {
    approvedVisualizationItems.value = [];
    return;
  }

  try {
    const list = await fetchApprovedVisualizationListApi();
    const subjectName = String(configStore.currentSubject?.subjectName ?? '').trim();
    const matched = list.filter((item: any) => {
      const itemSubjectId = String(item?.subject?.id ?? item?.subject_id ?? '');
      const itemSubjectName = String(item?.subject?.name ?? item?.subject_name ?? '').trim();
      if (!subjectId && !subjectName)
        return true;
      return itemSubjectId === subjectId || itemSubjectName === subjectName;
    });

    const sorted = [...matched].sort((a: any, b: any) => {
      const order1 = Number(a?.display_order);
      const order2 = Number(b?.display_order);
      if (Number.isFinite(order1) && Number.isFinite(order2) && order1 !== order2)
        return order1 - order2;
      const t1 = resolveApprovedAtMs(a);
      const t2 = resolveApprovedAtMs(b);
      if (t1 !== t2)
        return t1 - t2;
      return Number(a?.id ?? Number.MAX_SAFE_INTEGER) - Number(b?.id ?? Number.MAX_SAFE_INTEGER);
    });

    sorted.forEach((item: any) => {
      const knowledgePointTags = Array.from(new Set(
        (Array.isArray(item?.knowledge_points) ? item.knowledge_points : [])
          .map((point: any) => String(point?.name || '').trim())
          .map((name: string) => name.replace(/^\d+(?:\.\d+)+[.、，,:：\s-]*/, '').trim())
          .filter(Boolean),
      ));
      if (knowledgePointTags.length > 0)
        localStorage.setItem(`experiment-tags:v2:${item.id}`, JSON.stringify(knowledgePointTags));
    });

    approvedVisualizationItems.value = sorted.map((item: any) => ({
      approvedAtMs: resolveApprovedAtMs(item),
      visualizationId: Number(item.id),
      visualizationChapterId: Number(item?.chapter?.id ?? item?.chapter_id),
      displayOrder: Number(item?.display_order ?? 0),
      canReorder: item?.can_reorder === true,
      chapterName: String(item?.chapter?.name ?? item?.chapter_name ?? '其他章节'),
      expId: normalizeVisualizationExpId(item?.number ?? item?.exp_id ?? item?.expId),
      title: String(item.name || `可视化实验-${item.id}`),
      icon: 'lucide:flask-conical',
      route: `/experiment/visualization/${item.id}`,
    }));

    try {
      const remoteOrder = await fetchVisualizationMenuOrderApi(subjectId);
      if (Object.keys(remoteOrder).length > 0) {
        localStorage.setItem(getExperimentOrderStorageKey(), JSON.stringify(remoteOrder));
        experimentOrderVersion.value += 1;
      }
    }
    catch (error) {
      console.warn('读取服务器实验顺序失败，继续使用本机顺序', error);
    }
  }
  catch (e) {
    console.error('刷新可视化实验目录失败', e);
    approvedVisualizationItems.value = [];
  }
}

function toItemTree(root: Chapter): DrawerItem {
  interface AnyKnowledgePoint { id: string | number; name: string }
  interface AnyNode {
    id: string | number;
    name: string;
    children?: AnyNode[];
    knowledge_points?: AnyKnowledgePoint[];
  }
  const project = (node: any): AnyNode => ({
    id: node.id,
    name: node.name,
    knowledge_points: Array.isArray(node.knowledge_points) ? node.knowledge_points : [],
    children: node.children?.map(project) ?? [],
  });

  const rootProjected = project(root);

  const mapNode = (node: AnyNode, nearestChapterId: string | number = node.id): DrawerItem => {
    const childItems = (node.children ?? []).map(child => mapNode(child, child.id));
    const knowledgePointItems = (node.knowledge_points ?? [])
      .filter(point => String(point?.name ?? '').trim())
      .map(point => ({
        title: String(point.name).trim(),
        icon: 'lucide:circle-dot',
        chapterId: String(nearestChapterId),
      }));
    const isLeaf = childItems.length === 0 && knowledgePointItems.length === 0;
    const item: DrawerItem = {
      title: node.name,
      icon: isLeaf ? 'lucide:notebook' : 'lucide:folder',
    };
    if (isLeaf) {
      item.chapterId = String(node.id);
    }
    else {
      item.children = [...childItems, ...knowledgePointItems];
    }
    return item;
  };

  const chapter = mapNode(rootProjected);
  chapter.children?.unshift({
    title: '思维导图',
    icon: 'lucide:chart-pie',
    chapterId: root.id,
  });

  return regroupRelatedExamples(chapter);
}

async function refreshQuestionList() {
  if (!configStore.currentSubject?.id_) {
    questionItems.value = [];
    dynamicCourseChapters.value = [];
    return;
  }
  try {
    const response = await fetchChapterListApi(configStore.currentSubject.id_);
    const chapterList = response.chapters;
    questionItems.value = chapterList.map(chapter => toItemTree(chapter));
    dynamicCourseChapters.value = isDynamicCourse.value ? chapterList : [];
  }
  catch (error) {
    console.error('Error tracking button click:', error);
    questionItems.value = [];
    dynamicCourseChapters.value = [];
  }
}

function visualizationSubjectId(item: VisualizationResource) {
  return String(item.subject?.id ?? item.subject_id ?? '');
}

function visualizationChapterName(item: VisualizationResource) {
  return String(item.chapter?.name ?? item.chapter_name ?? '未指定章节');
}

function buildLocalPreviewVisualizations(): VisualizationResource[] {
  const chapters = dynamicCourseChapters.value;
  const common = {
    courseId: String(configStore.currentSubject?.id_ ?? 0),
    courseName: String(configStore.currentSubject?.name ?? '自建课程'),
  };
  return [
    buildPreviewVisualization({
      ...common,
      chapterId: chapters[0]?.id,
      chapterName: chapters[0]?.name || '第一章 概率论的基本概念',
      visualizationId: PREVIEW_VISUALIZATION_ID,
    }),
    buildPreviewVisualization({
      ...common,
      chapterId: chapters[0]?.id,
      chapterName: chapters[0]?.name || '第一章 概率论的基本概念',
      visualizationId: SECOND_PREVIEW_VISUALIZATION_ID,
    }),
    buildPreviewVisualization({
      ...common,
      chapterId: chapters[1]?.id,
      chapterName: chapters[1]?.name || '第二章 随机变量及其分布',
      visualizationId: THIRD_PREVIEW_VISUALIZATION_ID,
    }),
  ];
}

function hasPendingVisualization(item: DrawerItem): boolean {
  if ((item as any).reviewStatus === 'pending')
    return true;
  return Array.isArray(item.children) && item.children.some(child => hasPendingVisualization(child));
}

async function refreshPendingCourseVisualizations() {
  if (!isTeacherCoursePreview.value || !configStore.currentSubject?.id_) {
    pendingCourseVisualizations.value = [];
    return;
  }

  const previewIds = new Set([PREVIEW_VISUALIZATION_ID, SECOND_PREVIEW_VISUALIZATION_ID, THIRD_PREVIEW_VISUALIZATION_ID]);
  pendingCourseVisualizations.value = pendingCourseVisualizations.value.filter(item => !previewIds.has(item.id));
  approvedVisualizationItems.value = approvedVisualizationItems.value.filter(item => !previewIds.has(item.visualizationId));

  loadingPendingCourseVisualizations.value = true;
  try {
    const subjectId = String(configStore.currentSubject.id_);
    const list = await fetchVisualizationListApi({
      subject_id: subjectId,
      review_status: 'pending',
    });
    pendingCourseVisualizations.value = list.filter(item =>
      String(item.review_status || '').toLowerCase() === 'pending'
      && visualizationSubjectId(item) === subjectId,
    );
  }
  catch (error) {
    console.warn('读取课程待审核可视化资源失败', error);
    pendingCourseVisualizations.value = [];
  }
  finally {
    if (import.meta.env.MODE === 'local-acceptance' && isVisualizationPreviewCourse.value) {
      const previews = buildLocalPreviewVisualizations();
      const previewIds = new Set(previews.map(preview => preview.id));
      approvedVisualizationItems.value = approvedVisualizationItems.value.filter(item => !previewIds.has(item.visualizationId));
      const existingPendingIds = new Set(pendingCourseVisualizations.value.map(item => item.id));
      previews.filter(preview => preview.review_status === 'pending' && !existingPendingIds.has(preview.id)).forEach((preview) => {
        pendingCourseVisualizations.value.push(preview);
      });
      previews.filter(preview => preview.review_status === 'approved').forEach((preview) => {
        approvedVisualizationItems.value.push({
          approvedAtMs: Date.now(),
          visualizationId: preview.id,
          visualizationChapterId: Number(preview.chapter?.id ?? 0),
          displayOrder: Number.MAX_SAFE_INTEGER,
          canReorder: false,
          chapterName: visualizationChapterName(preview),
          expId: String(preview.number || ''),
          title: preview.name,
          icon: 'lucide:flask-conical',
          route: `/experiment/visualization/${preview.id}`,
        });
      });
    }
    loadingPendingCourseVisualizations.value = false;
  }
}

async function refreshOwnVisualizationReviewProgress() {
  if (Number(userStore.user?.role) === 2 || Number(userStore.user?.role) === 9) {
    rejectedOwnVisualizationCount.value = 0;
    return;
  }

  try {
    const response = await fetchMyVisualizationApi();
    const payload = response as any;
    const list = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.results)
        ? payload.results
        : [];
    rejectedOwnVisualizationCount.value = list.filter(
      (item: any) => String(item?.review_status || '').toLowerCase() === 'rejected',
    ).length;
  }
  catch (error) {
    console.warn('读取我的可视化审核进度失败', error);
    rejectedOwnVisualizationCount.value = 0;
  }
}

async function refreshSubjectList() {
  try {
    await fetchSubjectListApi();
  }
  catch (error) {
    console.error('Error tracking button click:', error);
  }
}

function syncProbabilityDistributionItem() {
  sideBarItem.value = sideBarItem.value.filter(item => item.route !== '/mindmap/distribution');
  if (configStore.currentSubjectId === 'probability') {
    sideBarItem.value.push({
      label: '分布关系',
      icon: Network,
      route: '/mindmap/distribution',
    });
  }
}

onMounted(() => {
  // TODO: 先获取所有subject，根据subjectId获取所有chapter，用chapterId获取章节内所有question
  refreshSubjectList();
  refreshQuestionList();
  refreshApprovedVisualizationItems();
  refreshPendingCourseVisualizations();
  refreshOwnVisualizationReviewProgress();
  window.addEventListener(PREVIEW_REVIEW_CHANGED_EVENT, refreshPendingCourseVisualizations);
  window.addEventListener('visualization-review-progress-changed', refreshOwnVisualizationReviewProgress);

  const role = useUserStore().user?.role ?? 0;
  if (role > 1) {
    sideBarItem.value.push({
      label: '班级学情',
      icon: Codepen,
      route: '/class-analysis',
    });
  }
  else if (role === 1) {
    sideBarItem.value.push({
      label: '学情面板',
      icon: User,
      route: '/analysis',
    });
  }
  syncProbabilityDistributionItem();
});

watch(isDynamicCourse, (dynamic) => {
  if (dynamic)
    openExperimentDrawer.value = false;
}, { immediate: true });

watch(() => configStore.currentSubject?.id_, () => {
  syncProbabilityDistributionItem();
  refreshQuestionList();
  refreshApprovedVisualizationItems();
  refreshPendingCourseVisualizations();
});

onBeforeUnmount(() => {
  window.removeEventListener(PREVIEW_REVIEW_CHANGED_EVENT, refreshPendingCourseVisualizations);
  window.removeEventListener('visualization-review-progress-changed', refreshOwnVisualizationReviewProgress);
});

function updateSubject() {
  subjectSelector.value?.updateConfigSubject();
  if (typeof document !== 'undefined') {
    document.location.reload();
  }
}

function openBackend() {
  if (typeof document !== 'undefined') {
    const a = document.createElement('a');
    a.href = 'https://umathhub.com/backend-dashboard/';
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.click();
  }
}
</script>

<template>
  <div class="relative">
    <aside class="h-full border rounded-xl flex flex-col relative bg-background z-50 items-center gap-4 p-3 shadow-lg">
      <!--      <Button -->
      <!--        size="icon" -->
      <!--        class="group rounded-full bg-primary text-lg font-semibold text-primary-foreground size-10 md:text-base" -->
      <!--        @click="goHome()" -->
      <!--      > -->
      <!--        <component -->
      <!--          :is="useConfigStore().currentSubject.icon" class="size-6 group-hover:scale-110 transition-all" @click="() => { -->
      <!--            openSubjectSelector = true; -->
      <!--          }" -->
      <!--        /> -->
      <!--      </Button> -->

      <!--      <img src="/logo-math.svg" alt="" class="size-10"> -->
      <div class="space-y-2 flex flex-col items-center">
        <div v-for="(item, index) in visibleSideBarItems" :key="index">
          <TooltipProvider v-if="!isVisitor() || visitorAllowedItem.includes(item.label)" :delay-duration="0">
            <Tooltip>
              <TooltipTrigger>
                <Button
                  size="icon" variant="ghost"
                  class="relative size-11 rounded-lg text-muted-foreground"
                  :aria-label="item.label"
                  :class="isSidebarItemActive(item) && '!bg-muted !text-primary'"
                  @click="() => {
                    if (item.command) item.command();
                    else if (item.route) router.push(item.route);
                    if (item.activeWhenDrawerOpen !== 'experiment') openExperimentDrawer = false;
                    if (item.activeWhenDrawerOpen !== 'question') openQuestionDrawer = false;
                  }"
                >
                  <component :is="item.icon" class="size-6" :stroke-width="2" />
                  <span
                    v-if="item.activeWhenDrawerOpen === 'experiment' && isTeacherCoursePreview && pendingVisualizationReviewCount > 0"
                    class="absolute right-1 top-1 size-2.5 rounded-full bg-red-500 ring-2 ring-background"
                    aria-label="有可视化实验待审核"
                  />
                  <span
                    v-else-if="item.label === '可视化上传' && rejectedOwnVisualizationCount > 0"
                    class="absolute right-1 top-1 size-2.5 rounded-full bg-red-500 ring-2 ring-background"
                    aria-label="有被退回的可视化实验"
                  />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>{{ item.label }}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      <div class="mt-auto w-full flex flex-col items-center gap-2">
        <TooltipProvider v-if="isBackendAllowed" :delay-duration="0">
          <Tooltip>
            <TooltipTrigger>
              <Button
                size="icon"
                variant="outline"
                class="size-11 rounded-lg"
                @click="openBackend"
              >
                <Server class="size-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>进入后端</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <TooltipProvider v-if="isTeacherCoursePreview" :delay-duration="0">
          <Tooltip>
            <TooltipTrigger>
              <Button
                size="icon"
                class="size-11 rounded-lg border-0 bg-blue-600 text-white shadow-sm shadow-blue-200/70 hover:bg-blue-500"
                @click="openCourseWorkspace"
              >
                <BriefcaseBusiness class="size-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>进入课程工作台</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <TooltipProvider :delay-duration="0">
          <Tooltip>
            <TooltipTrigger>
              <Button
                size="icon"
                variant="outline"
                class="size-11 rounded-lg"
                @click="router.push('/subject-links')"
              >
                <Link2 class="size-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>学科资讯链接</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </aside>

    <!-- experiment drawer -->
    <div class="absolute left-full ml-2 top-0 z-50 transition-all bottom-0 rounded-xl overflow-y-auto border w-96 bg-background shadow-xl" :class="!openExperimentDrawer && 'opacity-0 pointer-events-none'">
      <div v-if="canReorderVisualizations" class="mx-3 mt-3 rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
        拖动可视化实验左侧手柄，可调整同一章节内的显示顺序
      </div>
      <div
        v-if="openExperimentOrderConfirm"
        class="sticky top-2 z-20 mx-3 mt-2 rounded-lg border border-primary/30 bg-background p-3 shadow-lg"
      >
        <div class="text-sm font-semibold">
          确认调整实验顺序？
        </div>
        <p class="mt-1 text-xs leading-5 text-muted-foreground">
          将“{{ pendingExperimentOrder?.sourceTitle }}”移动到“{{ pendingExperimentOrder?.targetTitle }}”所在位置。
        </p>
        <div class="mt-3 flex justify-end gap-2">
          <Button size="sm" variant="outline" :disabled="isSavingVisualizationOrder" @click="cancelExperimentOrderChange">
            取消
          </Button>
          <Button size="sm" :disabled="isSavingVisualizationOrder" @click="confirmExperimentOrderChange">
            <Icon v-if="isSavingVisualizationOrder" icon="lucide:loader-2" class="mr-1 h-3.5 w-3.5 animate-spin" />
            {{ isSavingVisualizationOrder ? '保存中...' : '确认调整' }}
          </Button>
        </div>
      </div>
      <TreeRoot
        v-if="experimentItems && experimentItems.length"
        :key="`${configStore.currentSubject?.id_}:${experimentOrderVersion}`"
        v-slot="{ flattenItems }"
        v-auto-animate
        class="list-none select-none w-full rounded-lg px-3 py-2 text-base font-medium"
        :items="experimentItems"
        :get-key="(item: any) => item.title"
        :default-expanded="['components']"
      >
        <TreeItem
          v-for="item in flattenItems"
          v-slot="{ isExpanded }"
          :key="item._id"
          :style="{ 'padding-left': `${item.level - 0.5}rem` }"
          v-bind="item.bind"
          class="relative flex items-center p-2 cursor-pointer my-0.5 rounded outline-none focus:ring-primary focus:ring-2 data-[selected]:bg-muted"
          :class="{
            'opacity-50': getExperimentMenuKey(item.value) !== null && draggedVisualizationId === getExperimentMenuKey(item.value),
            'ring-2 ring-primary/50': getExperimentMenuKey(item.value) !== null && dragOverVisualizationId === getExperimentMenuKey(item.value),
          }"
          @click="handleExperimentItemClick(item.value)"
          @dragover="handleVisualizationDragOver($event, item.value)"
          @drop="handleVisualizationDrop($event, item.value)"
        >
          <span
            v-if="canDragVisualization(item.value)"
            draggable="true"
            class="mr-1 inline-flex h-6 w-5 shrink-0 cursor-grab items-center justify-center rounded text-muted-foreground hover:bg-muted-foreground/10 active:cursor-grabbing"
            title="拖动调整顺序"
            @click.stop
            @pointerdown.stop
            @dragstart.stop="handleVisualizationDragStart($event, item.value)"
            @dragend.stop="handleVisualizationDragEnd"
          >
            <Icon icon="lucide:grip-vertical" class="h-4 w-4 pointer-events-none" />
          </span>
          <template v-if="item.hasChildren">
            <Icon
              v-if="!isExpanded"
              icon="lucide:folder"
              class="h-4 w-4"
            />
            <Icon
              v-else
              icon="lucide:folder-open"
              class="h-4 w-4"
            />
          </template>
          <Icon
            v-else
            :icon="item.value.icon || 'lucide:file'"
            class="h-4 w-4"
          />
          <div class="min-w-0 flex-1 pl-2">
            {{ item.value.title }}
          </div>
          <span
            v-if="(item.value as any).reviewStatus === 'pending'"
            class="ml-2 shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700"
          >
            待审核
          </span>
          <span
            v-else-if="item.hasChildren && hasPendingVisualization(item.value)"
            class="absolute right-1.5 top-1.5 size-2.5 rounded-full bg-red-500 ring-2 ring-background"
            aria-label="本章节有可视化实验待审核"
          />
        </TreeItem>
      </TreeRoot>

      <div v-else class="px-4 py-3 text-sm text-muted-foreground h-full flex justify-center items-center">
        <div class="flex items-center gap-2">
          <Icon icon="lucide:info" class="h-4 w-4" />
          <span>本课程的实验还在开发中哦</span>
        </div>
      </div>
    </div>
    <div v-if="openExperimentDrawer" class="left-full top-0 h-full absolute z-40 w-screen" @click="openExperimentDrawer = false" />

    <!-- question drawer -->
    <div class="absolute left-full ml-2 top-0 z-50 transition-all bottom-0 rounded-xl overflow-y-auto border w-96 bg-background shadow-xl" :class="!openQuestionDrawer && 'opacity-0 pointer-events-none'">
      <TreeRoot
        v-if="questionItems && questionItems.length"
        v-slot="{ flattenItems }"
        v-auto-animate
        class="list-none select-none w-full rounded-lg px-3 py-2 text-base font-medium"
        :items="questionItems"
        :get-key="(item: any) => item.title"
        :default-expanded="['components']"
      >
        <TreeItem
          v-for="item in flattenItems"
          v-slot="{ isExpanded }"
          :key="item._id"
          :style="{ 'padding-left': `${item.level - 0.5}rem` }"
          v-bind="item.bind"
          class="flex items-center p-2 cursor-pointer my-0.5 rounded outline-none focus:ring-primary focus:ring-2 data-[selected]:bg-muted"
          @click="() => {
            if (item.value.chapterId) {
              router.push({
                name: item.value.title === '思维导图' ? 'QuestionDiagram' : 'DoQuestion',
                params: { chapterId: item.value.chapterId },
              });
              openQuestionDrawer = false;
            }
          }"
        >
          <template v-if="item.hasChildren">
            <Icon
              v-if="!isExpanded"
              icon="lucide:folder"
              class="h-4 w-4"
            />
            <Icon
              v-else
              icon="lucide:folder-open"
              class="h-4 w-4"
            />
          </template>
          <Icon
            v-else
            :icon="item.value.icon || 'lucide:file'"
            class="h-4 w-4"
          />
          <div class="pl-2">
            {{ item.value.title }}
          </div>
        </TreeItem>
      </TreeRoot>
      <div v-else class="px-4 py-3 text-sm text-muted-foreground h-full flex justify-center items-center">
        <div class="flex items-center gap-2">
          <Icon icon="lucide:info" class="h-4 w-4" />
          <span>本课程暂时还没有题目哦</span>
        </div>
      </div>
    </div>
    <div v-if="openQuestionDrawer" class="left-full top-0 h-full absolute w-screen z-40" @click="openQuestionDrawer = false" />

    <Dialog v-model:open="openSubjectSelector">
      <DialogContent class="max-w-4xl p-0 overflow-hidden">
        <DialogHeader class="px-6 pt-6">
          <DialogTitle>选择科目</DialogTitle>
          <DialogDescription>请选择您想要学习的科目。</DialogDescription>
        </DialogHeader>

        <div class="grid grid-cols-[3fr_1fr] gap-6 p-6 pt-2">
          <div class="space-y-3 md:pr-6 md:border-r">
            <BoxSelector ref="subjectSelector" :immediate="false" hint-color="text-muted-foreground" :subjects="universitySubjects" />
          </div>

          <div class="flex flex-col items-center justify-center gap-4">
            <div
              v-auto-animate
              class="w-full h-full rounded-xl border bg-muted/40 p-4 flex flex-col"
            >
              <div class="inline-flex items-center gap-2 text-sm text-muted-foreground">
                <span class="px-2 py-0.5 rounded-full bg-muted">即将进入</span>
              </div>

              <div class="mt-4 flex flex-col items-center justify-center flex-1 gap-3">
                <div
                  class="size-16 rounded-full flex items-center justify-center ring-2"
                  :style="{
                    color: '#171717',
                    boxShadow: '0 0 0 4px #e5e5e5',
                  }"
                >
                  <component
                    :is="subjectSelector?.currentSubject.icon"
                    class="size-8"
                  />
                </div>

                <div
                  class="text-2xl font-bold tracking-wide"
                  :style="{ color: '#171717' }"
                >
                  {{ subjectSelector?.currentSubject.name || '未选择科目' }}
                </div>

                <p class="text-sm text-muted-foreground">
                  将为您切换到所选科目的学习空间与题库。
                </p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter class="px-6 pb-6">
          <DialogClose>
            <Button
              class="mr-2"
              :style="{
                backgroundColor: '#171717',
                borderColor: '#171717',
                color: '#fff',
              }"
              @click="updateSubject"
            >
              确认
            </Button>
            <Button variant="outline">
              取消
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>

<style scoped></style>
